import { forwardRef } from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import {
  InputLabel,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';

import {
  RESOLVING_OPTION_LABEL,
  resolveSelectedOptionLabel,
} from '@/utils/selected-option-label';

export interface SelectInputOption {
  label: string;
  value: string;
}

export interface SelectInputProps {
  name: string;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorInfo?: string;
  options: SelectInputOption[];
  value: string;
  autoFocus?: boolean;
  onChange: (value: string) => void;
  /** true mientras se cargan las options; evita mostrar el campo como vacio. */
  loading?: boolean;
  /** Texto para un valor guardado que no figura en options. */
  unknownValueLabel?: string;
}

const SelectInput = forwardRef<HTMLInputElement, SelectInputProps>(({
  name,
  placeholder,
  errorInfo,
  required = false,
  error = false,
  disabled = false,
  options,
  value,
  autoFocus = false,
  onChange,
  loading = false,
  unknownValueLabel = 'Valor guardado',
}: SelectInputProps, ref) => {
  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value as string);
  };

  const handleClear = () => {
    onChange('');
  };

  const isKnownValue = !!value && options.some((opt) => opt.value === value);
  // Un valor guardado que todavia no figura en options no se blanquea: mostrar
  // el placeholder lo hace pasar por vacio y invita a pisarlo sin querer.
  const isUnresolvedValue = !!value && !isKnownValue;
  const unresolvedLabel = resolveSelectedOptionLabel(null, {
    isResolving: loading,
    fallback: unknownValueLabel,
  });

  return (
    <FormControl
      error={error}
      required={required}
      disabled={disabled}
      fullWidth
    >
      <InputLabel
        id={`${name}-label`}
        shrink={true}
      >
        {placeholder}
      </InputLabel>
      <Select
        id={name}
        inputRef={ref}
        labelId={`${name}-label`}
        variant='outlined'
        label={placeholder}
        value={value}
        onChange={handleChange}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return (
              <span className='text-gray-400 font-normal opacity-80'>
                {placeholder}
              </span>
            );
          }
          const option = options.find((opt) => opt.value === selected);
          if (option) return option.label;
          return (
            <span
              className={
                loading ? 'text-gray-400 font-normal opacity-80' : undefined
              }
            >
              {unresolvedLabel}
            </span>
          );
        }}
        autoFocus={autoFocus}
        endAdornment={
          value && !disabled ? (
            <InputAdornment position='end' sx={{ marginRight: '15px' }}>
              <IconButton
                aria-label='clear'
                onClick={handleClear}
                edge='end'
                size='small'
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      >
        <MenuItem value=''>
          <span className='text-gray-400 italic'>Seleccionar...</span>
        </MenuItem>
        {isUnresolvedValue && (
          <MenuItem value={value} sx={{ display: 'none' }}>
            {unresolvedLabel === RESOLVING_OPTION_LABEL
              ? RESOLVING_OPTION_LABEL
              : unknownValueLabel}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{errorInfo}</FormHelperText>
    </FormControl>
  );
});

SelectInput.displayName = 'SelectInput';

export default SelectInput;
